<template>
  <BaseArchive
    v-if="articles && meta"
    class="archive-category"
    :articles="articles"
    :link="link"
    :meta="meta"
    :class="rootClass"
    :slug="categorySlug"
  >
    <template #top>
      <div
        v-if="isTop"
        id="title-group-cover"
        class="archive-page__cover"
      />
    </template>

    <template #header>
      <BaseTitleGroup
        v-if="category"
        :rubric="category"
        :is-first-page="pageNumber === 1"
        :is-simple="isSimple"
      />
    </template>
  </BaseArchive>
</template>

<script lang="ts">
  import { ARCHIVES_ARTICLES_COUNT_ON_PAGE } from '~/constants';
  import BaseArchive from '~/components/BaseComponents/BaseArchive.vue';
  import BaseTitleGroup from '~/components/BaseComponents/BaseTitleGroup.vue';
  import { useSeoStore } from '~/store/seo';
  import { useWindowStore } from '~/store/window';
  import { useArchivesStore } from '~/store/archives';
  import { getRouteParam } from '~/types/utils/route';
  import { useWidgetsStore } from '~/store/widgets';
  import { useHeadWithSeo } from '~/composables/useHeadWithSeo';

  export default defineNuxtComponent({
    name: 'ArchiveCategory',

    components: {
      BaseArchive,
      BaseTitleGroup,
    },

    provide() {
      return {
        // для отправки аналитики
        eventCategory: () => 'Архивы Рубрик',
        eventActionComments: () => 'Переход к комментариям из архива рубрики',
        eventActionCategory: () => 'Переход к рубрике из архива рубрики',
        eventActionArticle: () => 'Переход из архива рубрики',
      };
    },

    scrollToTop: true,

    async setup() {
      const { $api } = useNuxtApp();
      const archiveStore = useArchivesStore();
      const seoStore = useSeoStore();
      const widgetsStore = useWidgetsStore();

      const route = useRoute();
      const categorySlug = getRouteParam(route.params.categorySlug);
      const pageNumber = +getRouteParam(route.params.pageNumber || 1);
      const pagesDataRoute = route.fullPath.replace(route.hash, '');

      if (!categorySlug) {
        throw createError({ statusCode: 404, message: 'Invalid categorySlug' });
      }

      const { data, error } = await useAsyncData('category-posts', async () =>
        $api.archives.category({
          slug: categorySlug,
          page: pageNumber,
          limit: ARCHIVES_ARTICLES_COUNT_ON_PAGE,
        }),
      );

      if (error.value || data.value?.data.meta.total_pages === 0) {
        throw createError({ statusCode: 404, message: 'No data' });
      }

      if (data.value?.data) {
        widgetsStore.setVisibilityWidgetsCondition([
          {
            type: 'category',
            value: data.value.data.item.id?.toString() ?? '',
          },
        ]);

        archiveStore.setMeta(data.value.data.meta);
        seoStore.addSeoByPath(pagesDataRoute, data.value.data.seo);
      }

      useHeadWithSeo();

      return {
        articles: data.value?.data.items,
        meta: data.value?.data.meta,
        category: data.value?.data.item,
      };
    },

    computed: {
      isDesktopAndMore() {
        return useWindowStore().isDesktopAndMore;
      },

      seo() {
        return useSeoStore().currentSeo;
      },

      categorySlug(): string {
        return getRouteParam(useRoute().params.categorySlug);
      },

      pageNumber(): number {
        return Number(useRoute().params.pageNumber) ? Number(useRoute().params.pageNumber) : 1;
      },

      link(): HTMLAnchorElement {
        return {
          href: `/category/${this.categorySlug}`,
        } as HTMLAnchorElement;
      },

      isTop(): boolean {
        return !!(this.isDesktopAndMore && this.pageNumber === 1 && this.category?.covers.length);
      },

      rootClass(): Record<string, boolean> {
        return {
          'archive-category--simple': this.isSimple,
        };
      },

      isSimple(): boolean {
        if (this.pageNumber > 1) {
          return true;
        }

        return (
          !this.category?.color &&
          this.category?.covers.length === 0 &&
          !this.category.parsed_description.length
        );
      },
    },
  });
</script>

// TODO перенести стили по категориям сюда с BaseArchive, после этого убрать импортанты
<style lang="scss" scoped>
  :deep() {
    .archive__pagination {
      padding: 8px 12px 24px;
      @include tablet {
        padding: 8px 18px;
      }
      @include big-tablet {
        padding: 8px 24px;
      }
      @include desktop {
        padding: 8px 32px;
      }
    }
    .archive__pagination--top {
      margin-top: 0;
      padding-top: 0;
      padding-bottom: 0;
      @include tablet {
        margin-top: 12px;
      }
      @include big-tablet {
        margin-top: 16px;
        margin-bottom: 20px;
      }
    }
    .archive__container {
      padding-top: 0 !important;
      padding-bottom: 0 !important;
      @include desktop {
        padding-bottom: 36px !important;
      }
    }
    .archive__content {
      padding-bottom: 0 !important;
      @include tablet {
        padding-bottom: 24px !important;
      }
      @include big-tablet {
        padding-bottom: 32px !important;
      }
      @include desktop {
        padding-bottom: 0 !important;
      }
    }
    .archive__header {
      padding: 0 !important;
      @include tablet {
        background-color: #fff;
        padding: 32px 32px 0 !important;
        border-radius: 48px 48px 0 0;
      }
      @include desktop {
        background-color: transparent;
        padding: 0 !important;
        border-radius: 0;
      }
    }
  }
</style>
